'use client';

import React, { forwardRef } from 'react';
import clsx from 'clsx';
import { type BasicShopUIPropTypes } from 'shop-ui/types';
import { MediaBackground } from 'shop-ui/component/MediaBackground';
import { BackgroundMask } from 'shop-ui/component/BackgroundMask';
import { useAspectRatioMinHeight } from 'shop-ui/tools/useAspectRatioMinHeight';
import { useCombinedRefs } from 'shop-ui/tools/useCombinedRefs';
import './Container.scss';

interface ContainerProps extends BasicShopUIPropTypes {}

export const Container = forwardRef<HTMLDivElement, ContainerProps>((props, ref) => {
    const {
        children,
        className,
        style,
        id,
        type,
        backgroundImageURL,
        backgroundVideoURL,
        mediaAspectRatio,
        mediaFitType,
        backgroundMaskColor,
        defaultBackgroundImageURL,
        styleMap,
        ...restProps
    } = props;

    const localRef = useCombinedRefs(ref);

    const { minHeight, aspectRatio } = useAspectRatioMinHeight(mediaAspectRatio, {
        externalRef: localRef,
        updateThreshold: 0.1,
    });

    return (
        <div
            id={id}
            ref={localRef}
            style={{
                ...style,
                ...(minHeight && {
                    '--shop-ui-container-min-height': `${minHeight}px`,
                }),
                ...(aspectRatio && {
                    'aspect-ratio': aspectRatio,
                }),
            }}
            className={clsx('container', className)}
            data-shop-component-container-id={id}
            {...restProps}
        >
            {/* 等 image-set 普及後可考慮替換掉圖片背景，https://developer.mozilla.org/en-US/docs/Web/CSS/image/image-set */}
            {(backgroundImageURL || backgroundVideoURL || defaultBackgroundImageURL) && (
                // @ts-ignore
                <MediaBackground
                    type={type}
                    defaultSource={defaultBackgroundImageURL}
                    source={backgroundImageURL || backgroundVideoURL}
                    {...((backgroundImageURL || defaultBackgroundImageURL) && {
                        mediaType: 'image',
                    })}
                    {...(backgroundVideoURL && {
                        mediaType: 'video',
                    })}
                />
            )}
            {backgroundMaskColor && <BackgroundMask mask={backgroundMaskColor} />}
            <div
                className={clsx(
                    'container-inner',
                    minHeight && 'container-inner--min-height'
                )}
                data-shop-component-container-inner
            >
                {children}
            </div>
        </div>
    );
});

Container.displayName = 'Container';
